import { Provider } from 'urql';
import { useState } from 'react';
import { appWithTranslation } from 'next-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { DSAriaLiveRegions, DSToastProvider } from '@stihl-design-system/components';

import { createGraphQLClient } from '@phoenix/core/frontend/shared/domain/http/graphql.service';
import { nextI18nConfig } from '@phoenix/core/frontend/shared/domain/i18n/i18n-config';
import { Scaffold } from '@phoenix/core/frontend/shared/feature/scaffold/scaffold';
import { ErrorLogger } from '@phoenix/core/frontend/shared/feature/error-logger';

import { AppAvailable } from '@phoenix/team-m/dashboard/frontend/shared/app-available';
import { UserPreferencesContextProvider } from '@phoenix/team-m/dashboard/frontend/shared/use-user-preferences-context';

import { toolShedCspDirectives } from '@phoenix/team-m/my-tool-shed/frontend/domain/security/csp';
import { NotificationContextProvider } from '@phoenix/team-m/my-tool-shed/frontend/shared/use-notification-context';

import { Layout } from '../components/layout';
import { CustomAppProps } from '../models/app.model';

import './styles.scss';
import 'swiper/swiper-bundle.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

if (!process.env.NEXT_PUBLIC_TOOL_SHED_GRAPHQL_BASE_URL) {
  throw new Error('NEXT_PUBLIC_TOOL_SHED_GRAPHQL_BASE_URL is not defined');
}
const client = createGraphQLClient(process.env.NEXT_PUBLIC_TOOL_SHED_GRAPHQL_BASE_URL);

function CustomApp({ Component, pageProps }: CustomAppProps) {
  const { header, footer, assets, navigation, page } = pageProps;
  const [ldContextReady, setLdContextReady] = useState(false);

  return (
    <ErrorLogger>
      <QueryClientProvider client={queryClient}>
        <DSAriaLiveRegions />
        <DSToastProvider>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          <Scaffold
            navigation={{ ...navigation, hide: true }}
            footer={footer}
            header={{ ...header, hide: true }}
            cspDirectives={toolShedCspDirectives}
            onLDContextReady={() => setLdContextReady(true)}
            assets={assets}
            page={page}
          >
            <AppAvailable ldContextReady={ldContextReady}>
              <Layout navItems={navigation?.navItems}>
                <NotificationContextProvider>
                  <Provider value={client}>
                    <UserPreferencesContextProvider>
                      <Component {...pageProps} />
                    </UserPreferencesContextProvider>
                  </Provider>
                </NotificationContextProvider>
              </Layout>
            </AppAvailable>
          </Scaffold>
        </DSToastProvider>
      </QueryClientProvider>
    </ErrorLogger>
  );
}

export default appWithTranslation(CustomApp, nextI18nConfig());
